import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Phone,
  Share2,
  Tag,
  UserRound,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";
import { getPostBySlug, BLOG_POSTS } from "@/data/blogPosts";

const SITE_URL = "https://deepcleanrr.com";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogBody = ({ blocks }) => (
  <>
    {blocks.map((b, i) => {
      if (b.type === "h2") {
        return (
          <h2
            key={i}
            data-testid={`blog-h2-${i}`}
            className="mt-12 font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl"
          >
            {b.text}
          </h2>
        );
      }
      if (b.type === "p") {
        return (
          <p
            key={i}
            className="mt-5 font-body text-base font-medium leading-relaxed text-slate-700 md:text-lg"
          >
            {b.text}
          </p>
        );
      }
      if (b.type === "ul") {
        return (
          <ul key={i} className="mt-5 space-y-2.5">
            {b.items.map((it, j) => (
              <li
                key={j}
                className="flex items-start gap-3 font-body text-base font-medium text-slate-700 md:text-lg"
              >
                <span
                  className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600"
                  aria-hidden="true"
                />
                {it}
              </li>
            ))}
          </ul>
        );
      }
      if (b.type === "img") {
        return (
          <figure key={i} className="mt-10">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={b.src}
                alt={b.alt}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            {b.caption && (
              <figcaption className="mt-3 text-center font-body text-sm italic text-slate-500">
                {b.caption}
              </figcaption>
            )}
          </figure>
        );
      }
      return null;
    })}
  </>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <main className="bg-white">
        <SEO
          title="Post Not Found | Deep Clean Restroom Services"
          description="The requested blog post could not be found."
          canonical={`${SITE_URL}/blog`}
        />
        <Header />
        <section className="mx-auto max-w-3xl px-6 pb-24 pt-40 text-center md:px-12">
          <h1 className="font-heading text-4xl font-black tracking-tighter text-slate-900">
            Post not found.
          </h1>
          <p className="mt-4 font-body text-slate-600">
            That article doesn't exist or has been moved.
          </p>
          <Link
            to="/blog"
            className="mt-8 inline-flex h-12 items-center gap-2 rounded-md bg-blue-600 px-6 font-body text-sm font-bold text-white hover:bg-blue-700"
          >
            <ArrowLeft size={14} strokeWidth={2.2} />
            Back to blog
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  const canonical = `${SITE_URL}/blog/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: [post.coverImage],
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Organization",
      name: "Deep Clean Restroom Services",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Deep Clean Restroom Services",
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/favicon.ico`,
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    keywords: post.keywords?.join(", "),
  };

  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const handleShare = async () => {
    const data = {
      title: post.title,
      text: post.description,
      url: canonical,
    };
    try {
      if (navigator.share) await navigator.share(data);
      else await navigator.clipboard.writeText(canonical);
    } catch {
      /* ignore */
    }
  };

  return (
    <main data-testid="blog-post-page" className="bg-white">
      <SEO
        title={`${post.title} | Deep Clean Restroom Services`}
        description={post.description}
        canonical={canonical}
        image={post.coverImage}
        type="article"
        keywords={post.keywords}
        jsonLd={jsonLd}
      />
      <Header />

      {/* EDITORIAL ARTICLE HEADER */}
      <section className="relative isolate overflow-hidden bg-white pt-24">
        {/* Soft top tint */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px]"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, #F1F5F9 0%, #FFFFFF 100%)",
          }}
        />

        <div className="mx-auto max-w-7xl px-6 pb-16 pt-8 md:px-12 md:pb-20 md:pt-12">
          {/* Top breadcrumb / back row */}
          <div className="mb-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-body text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            <Link
              to="/blog"
              data-testid="back-to-blog-link"
              className="inline-flex items-center gap-1.5 text-blue-600 transition-colors hover:text-blue-700"
            >
              <ArrowLeft size={12} strokeWidth={2.4} />
              Blog
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-slate-500">{post.category}</span>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            {/* LEFT: Title + meta */}
            <div className="lg:col-span-7">
              <span
                data-testid="post-category-chip"
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-600/10 px-3 py-1 font-body text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                {post.category}
              </span>

              <h1
                data-testid="blog-post-title"
                className="mt-6 font-heading text-4xl font-black leading-[1.02] tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl"
              >
                {post.title}
              </h1>

              <p
                data-testid="post-deck"
                className="mt-6 max-w-xl font-body text-lg font-medium leading-relaxed text-slate-600 md:text-xl"
              >
                {post.description}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-200 pt-6 font-body text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                    <Calendar size={12} strokeWidth={2.4} />
                  </span>
                  {formatDate(post.date)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                    <Clock size={12} strokeWidth={2.4} />
                  </span>
                  {post.readTime}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                    <UserRound size={12} strokeWidth={2.4} />
                  </span>
                  Deep Clean Team
                </span>
                <button
                  onClick={handleShare}
                  data-testid="share-btn"
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                >
                  <Share2 size={12} strokeWidth={2.4} />
                  Share
                </button>
              </div>
            </div>

            {/* RIGHT: Cover image, framed and offset */}
            <div className="relative lg:col-span-5">
              <div className="relative">
                {/* Decorative blue offset block */}
                <div
                  className="absolute -inset-x-3 -inset-y-3 -z-10 hidden rounded-md bg-blue-600 lg:block"
                  style={{ transform: "translate(12px, 12px)" }}
                  aria-hidden="true"
                />
                <div
                  data-testid="post-cover-image-wrap"
                  className="relative aspect-[4/5] overflow-hidden rounded-md border border-slate-200 bg-slate-100 shadow-xl shadow-slate-900/10"
                >
                  <img
                    src={post.coverImage}
                    alt={post.coverAlt}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {/* Bottom-anchored caption pill */}
                  <div className="absolute inset-x-4 bottom-4 flex items-center gap-2 rounded-md bg-slate-950/85 px-4 py-2.5 text-white backdrop-blur">
                    <span className="grid h-7 w-7 place-items-center rounded-md bg-cyan-300/15 text-cyan-300">
                      <Tag size={12} strokeWidth={2.4} />
                    </span>
                    <span className="font-body text-[11px] font-bold uppercase tracking-[0.2em]">
                      Wilmington, NC
                    </span>
                  </div>
                </div>
              </div>

              {/* Stat strip beneath image */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-md border border-slate-200 bg-white p-4">
                  <p className="font-heading text-2xl font-black tracking-tighter text-slate-900">
                    7+
                  </p>
                  <p className="mt-1 font-body text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                    Common Health Risks
                  </p>
                </div>
                <div className="rounded-md border border-slate-200 bg-white p-4">
                  <p className="font-heading text-2xl font-black tracking-tighter text-slate-900">
                    100%
                  </p>
                  <p className="mt-1 font-body text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                    Eliminated by Deep Clean
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <article
        data-testid="blog-post-body"
        className="bg-white py-16 md:py-24"
      >
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <BlogBody blocks={post.body} />
        </div>
      </article>

      {/* CTA */}
      <section className="relative bg-slate-950 py-16 text-white md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Wilmington, NC
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">
              Ready for restrooms your customers will actually want to use?
            </h2>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="post-cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white transition-colors hover:bg-blue-700"
            >
              Book a Cleaning
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="post-cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white transition-colors hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {others.length > 0 && (
        <section className="border-t border-slate-200 bg-white py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — More from the journal
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Keep reading.
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
              {others.map((p, i) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${i}`}
                  className="group flex flex-col overflow-hidden rounded-md border border-slate-200 transition-colors hover:border-blue-600"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img
                      src={p.coverImage}
                      alt={p.coverAlt || p.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-body text-xs font-semibold uppercase tracking-wider text-blue-600">
                      {p.category}
                    </p>
                    <h3 className="mt-3 font-heading text-xl font-bold tracking-tight text-slate-900 group-hover:text-blue-600">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
};

export default BlogPostPage;
